import type { Editor } from '@tiptap/core';
import { atom, useAtom } from 'jotai';
import type { JSONContent } from 'novel';
import { useMemo } from 'react';

const editorContentAtom = atom<JSONContent | null>(null);

// Create a type for our TiptapState
type TiptapState = {
  editor: Editor | null;
  entityId: string | null;
};

// Replace the simple editor atom with a more comprehensive state
const tiptapStateAtom = atom<TiptapState>({
  editor: null,
  entityId: null,
});

export const useEditorContent = () => {
  const [tiptapState] = useAtom(tiptapStateAtom);
  const { editor } = tiptapState;

  const [editorContent, setEditorContent] = useAtom(editorContentAtom);

  const text = useMemo(() => {
    if (!editorContent) return '';

    // Properly handle paragraphs and their content
    if (!editorContent.content) return '';

    return editorContent.content
      .map((node) => {
        // Handle paragraph nodes
        if (node.type === 'paragraph' && node.content) {
          return node.content
            .map((contentNode) => {
              if (contentNode.type === 'text') {
                return contentNode.text;
              }
              if (contentNode.type === 'hardBreak') {
                return '\n';
              }
              return '';
            })
            .join('');
        }
        // Handle direct text nodes or other node types
        return node.text || '';
      })
      .join('\n'); // Join paragraphs with single newlines
  }, [editorContent]);

  const setTextValue = (text: string) => {
    // Split the text by newlines and preserve empty lines
    const lines = text.split('\n');

    // Create content with paragraphs, including empty ones to preserve spacing
    const content = {
      type: 'doc',
      content: lines.map((line) => ({
        type: 'paragraph',
        content: line.trim() !== '' ? [{ type: 'text', text: line }] : [],
      })),
    };

    // Set the editor content with the multi-paragraph structure
    editor?.commands.setContent(content);
    setEditorContent(content);
  };

  return {
    editorContent,
    setEditorContent,
    text,
    setTextValue,
  };
};

export const useChatEditorTiptap = () => {
  const [tiptapState, setTiptapState] = useAtom(tiptapStateAtom);
  const { editor, entityId } = tiptapState;

  // Helper to update just the editor portion of the state
  const setEditor = (newEditor: Editor | null) => {
    setTiptapState((prev) => ({ ...prev, editor: newEditor }));
  };

  // Helper to update just the chatbotId portion of the state
  const setEntityId = (newEntityId: string | null) => {
    setTiptapState((prev) => ({ ...prev, entityId: newEntityId }));
  };

  const cleanupEditor = () => {
    editor?.commands.setContent(null);
  };

  const setContent = (content: JSONContent) => {
    editor?.commands.setContent(content);
  };

  const removeMention = (id: string) => {
    // Find and remove the mention with the specified ID
    editor?.state.doc.descendants((node, pos) => {
      if (node.type.name === 'mention' && node.attrs.id === id) {
        editor?.commands.deleteRange({ from: pos, to: pos + node.nodeSize });
        return false; // Stop traversing once found
      }
      return true;
    });

    // Return the new editor content
    return editor?.getJSON();
  };

  return {
    editor,
    entityId,
    setEditor,
    setEntityId,
    cleanupEditor,
    setContent,
    removeMention,
  };
};
